import {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";

/*
    What each question type is, one row per type, so a new type fails the typecheck here instead of quietly reading
    as "other" wherever types are listed. No JSX: plain .ts utils such as the validator and the visibility vocabulary
    read it, and a .tsx would pull React and the type icons into each of them.
*/

/** What the answer is made of. 'none' = a screen (welcome, end): frames the survey, collects nothing. */
export type SurveyAnswerKind = 'none' | 'number' | 'boolean' | 'options' | 'text' | 'date' | 'media';

/** A fixed whole-number rating; its answer kind is always 'number'. */
export interface SurveyRatingScale {min: number; max: number}

export interface SurveyQuestionTypeFacts {
    answer: SurveyAnswerKind;
    scale: SurveyRatingScale | null;
    /** Offers the guest-written "Other" choice (`allowOther`). */
    otherOption: boolean;
    /** The author can allow several options (`allowMultiple`, off on a new question). */
    multipleSelection: boolean;
}

/** What a row leaves out, its type doesn't have. */
const BASE: Omit<SurveyQuestionTypeFacts, 'answer'> = {scale: null, otherOption: false, multipleSelection: false};

const SURVEY_QUESTION_TYPE_FACTS: Record<SurveyQuestionType, SurveyQuestionTypeFacts> = {
    [SurveyQuestionType.WELCOME]: {...BASE, answer: 'none'},
    [SurveyQuestionType.GENERIC_SCORE]: {...BASE, answer: 'number', scale: {min: 1, max: 10}},
    [SurveyQuestionType.GENERIC_STARS]: {...BASE, answer: 'number', scale: {min: 1, max: 5}},
    [SurveyQuestionType.MULTIPLE_CHOICE]: {...BASE, answer: 'options', otherOption: true, multipleSelection: true},
    [SurveyQuestionType.PICTURE_CHOICE]: {...BASE, answer: 'options', multipleSelection: true},
    [SurveyQuestionType.DROPDOWN]: {...BASE, answer: 'options', otherOption: true},
    [SurveyQuestionType.YES_NO]: {...BASE, answer: 'boolean'},
    [SurveyQuestionType.SHORT_TEXT]: {...BASE, answer: 'text'},
    [SurveyQuestionType.LONG_TEXT]: {...BASE, answer: 'text'},
    [SurveyQuestionType.DATE]: {...BASE, answer: 'date'},
    [SurveyQuestionType.NUMBER]: {...BASE, answer: 'number'},
    [SurveyQuestionType.CHECKLIST]: {...BASE, answer: 'options'},
    [SurveyQuestionType.LEGAL]: {...BASE, answer: 'boolean'},
    [SurveyQuestionType.UPLOAD_MEDIA]: {...BASE, answer: 'media'},
    [SurveyQuestionType.END_SCREEN]: {...BASE, answer: 'none'},
};

/** undefined for a missing type and for a type this client doesn't know, like the existing getters. */
export const getSurveyQuestionTypeFacts = (
    type: SurveyQuestionType | null | undefined
): SurveyQuestionTypeFacts | undefined => (
    type ? SURVEY_QUESTION_TYPE_FACTS[type] : undefined
);

export const isScreenType = (type: SurveyQuestionType | null | undefined) => (
    getSurveyQuestionTypeFacts(type)?.answer === 'none'
);
